/**
 * Location Utilities
 * Reusable functions for resolving location names from IDs
 */

import type { CountryInfo, StateInfo, CityInfo } from '@/types/api/location';

/**
 * Input type for location IDs that may need sanitization
 */
export interface LocationIdsInput {
  countryId?: number | null;
  stateId?: number | null;
  cityId?: number | null;
}

/**
 * Output type for sanitized location IDs ready for API requests
 */
export interface SanitizedLocationIds {
  countryId: number | undefined;
  stateId: number | undefined;
  cityId: number | undefined;
}

/**
 * Sanitizes location IDs to ensure they are valid numbers for API requests.
 * Converts null, undefined, strings, or other invalid values to undefined.
 *
 * Backend APIs require proper numeric IDs for country/state/city lookups.
 * Sending invalid values (null, strings, etc.) can cause 500 errors.
 *
 * @param input - Object containing countryId, stateId, cityId that may be invalid
 * @returns Object with sanitized IDs (number or undefined)
 *
 * @example
 * ```tsx
 * const { countryId, stateId, cityId } = sanitizeLocationIds({
 *   countryId: contact.countryId,
 *   stateId: contact.stateId,
 *   cityId: contact.cityId,
 * });
 *
 * await api.createContact({
 *   ...otherData,
 *   countryId,
 *   stateId,
 *   cityId,
 * });
 * ```
 */
export function sanitizeLocationIds(input: LocationIdsInput): SanitizedLocationIds {
  return {
    countryId: typeof input.countryId === 'number' && input.countryId > 0 ? input.countryId : undefined,
    stateId: typeof input.stateId === 'number' && input.stateId > 0 ? input.stateId : undefined,
    cityId: typeof input.cityId === 'number' && input.cityId > 0 ? input.cityId : undefined,
  };
}

export interface LocationData {
  countryId?: number | null;
  country?: string;
  stateId?: number | null;
  state?: string;
  cityId?: number | null;
  city?: string;
}

export interface ResolvedLocationData {
  country: string;
  stateId: number | null;
  state: string;
  cityId: number | null;
  city: string;
}

/**
 * Resolves location names from IDs using loaded country/state/city data
 *
 * @param data - Object containing countryId, stateId, cityId (and optionally country, state, city names)
 * @param countries - Array of loaded countries
 * @param states - Array of loaded states (for the selected country)
 * @param cities - Array of loaded cities (for the selected state)
 * @returns Object with resolved location names
 *
 * @example
 * ```tsx
 * const resolved = resolveLocationNames(
 *   { countryId: 1, stateId: 5, cityId: 10 },
 *   countries,
 *   states,
 *   cities
 * );
 * // Returns: { country: 'USA', stateId: 5, state: 'California', cityId: 10, city: 'San Jose' }
 * ```
 */
export function resolveLocationNames(
  data: LocationData | null | undefined,
  countries: CountryInfo[],
  states: StateInfo[],
  cities: CityInfo[]
): ResolvedLocationData {
  if (!data) {
    return {
      country: '',
      stateId: null,
      state: '',
      cityId: null,
      city: '',
    };
  }

  // Resolve country
  let countryCode = data.country || '';
  if (!countryCode && data.countryId) {
    const country = countries.find(c => c.countryId === data.countryId);
    countryCode = country?.countryCode || '';
  }

  // Resolve state
  let stateName = data.state || '';
  const stateId = data.stateId || null;
  if (!stateName && stateId && states.length > 0) {
    const state = states.find(s => s.stateId === stateId);
    stateName = state?.stateName || '';
  }

  // Resolve city
  let cityName = data.city || '';
  const cityId = data.cityId || null;
  if (!cityName && cityId && cities.length > 0) {
    const city = cities.find(c => c.cityId === cityId);
    cityName = city?.cityName || '';
  }

  return {
    country: countryCode,
    stateId,
    state: stateName,
    cityId,
    city: cityName,
  };
}

/**
 * Checks if cascading location data is fully loaded for the given IDs
 *
 * @param data - Object containing countryId, stateId, cityId
 * @param countries - Array of loaded countries
 * @param states - Array of loaded states
 * @param cities - Array of loaded cities
 * @returns true if all required data is loaded, false otherwise
 *
 * @example
 * ```tsx
 * const isReady = isLocationDataReady(
 *   { countryId: 1, stateId: 5, cityId: 10 },
 *   countries,
 *   states,
 *   cities
 * );
 * if (isReady) {
 *   // Safe to resolve names
 *   const resolved = resolveLocationNames(...);
 * }
 * ```
 */
export function isLocationDataReady(
  data: LocationData | null | undefined,
  countries: CountryInfo[],
  states: StateInfo[],
  cities: CityInfo[]
): boolean {
  if (!data) return true; // No data to load

  // Check if country data is loaded
  if (countries.length === 0) return false;

  // If there's a stateId, check if states are loaded
  if (data.stateId && states.length === 0) return false;

  // If there's a cityId, check if cities are loaded
  if (data.cityId && cities.length === 0) return false;

  return true;
}

/**
 * Gets the country code from a country ID
 */
export function getCountryCode(countryId: number | null | undefined, countries: CountryInfo[]): string {
  if (!countryId) return '';
  const country = countries.find(c => c.countryId === countryId);
  return country?.countryCode || '';
}

/**
 * Gets the state name from a state ID
 */
export function getStateName(stateId: number | null | undefined, states: StateInfo[]): string {
  if (!stateId) return '';
  const state = states.find(s => s.stateId === stateId);
  return state?.stateName || '';
}

/**
 * Gets the city name from a city ID
 */
export function getCityName(cityId: number | null | undefined, cities: CityInfo[]): string {
  if (!cityId) return '';
  const city = cities.find(c => c.cityId === cityId);
  return city?.cityName || '';
}

/**
 * Gets the country name from a country ID
 */
export function getCountryName(countryId: number | null | undefined, countries: CountryInfo[]): string {
  if (!countryId) return '';
  const country = countries.find(c => c.countryId === countryId);
  return country?.countryName || country?.countryCode || '';
}

/**
 * Address data for formatting
 */
export interface AddressForDisplay {
  address?: string | null;
  city?: string | null;
  state?: string | null;
  postcode?: string | null;
  country?: string | null;
}

/**
 * Format address parts into a single display string.
 * Builds a comma-separated string from address components.
 *
 * @param data - Address components
 * @returns Formatted address string or '-' if empty
 *
 * @example
 * ```tsx
 * formatAddressForDisplay({
 *   address: '123 Main St',
 *   city: 'Taipei',
 *   state: 'Taipei',
 *   postcode: '100',
 *   country: 'Taiwan'
 * });
 * // Returns: "123 Main St, Taipei, Taipei, 100, Taiwan"
 * ```
 */
export function formatAddressForDisplay(
  data: AddressForDisplay,
  fallback: string = '-'
): string {
  const parts: string[] = [];

  if (data.address) parts.push(data.address);
  if (data.city) parts.push(data.city);
  if (data.state) parts.push(data.state);
  if (data.postcode) parts.push(data.postcode);
  if (data.country) parts.push(data.country);

  return parts.length > 0 ? parts.join(', ') : fallback;
}

/**
 * Format a full address with resolved location names from IDs.
 *
 * @param data - Address data with IDs
 * @param countries - Available countries array
 * @param states - Available states array (for the relevant country)
 * @param cities - Available cities array (for the relevant state)
 * @returns Formatted address string
 */
export function formatAddressWithResolvedNames(
  data: {
    address?: string | null;
    cityId?: number | null;
    city?: string | null;
    stateId?: number | null;
    state?: string | null;
    postcode?: string | null;
    countryId?: number | null;
    country?: string | null;
  },
  countries: CountryInfo[],
  states: StateInfo[],
  cities: CityInfo[],
  fallback: string = '-'
): string {
  // Resolve names from IDs if not provided
  const city = data.city || getCityName(data.cityId, cities);
  const state = data.state || getStateName(data.stateId, states);
  const country = data.country || getCountryName(data.countryId, countries);

  return formatAddressForDisplay(
    {
      address: data.address,
      city,
      state,
      postcode: data.postcode,
      country,
    },
    fallback
  );
}
